import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Image } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

import { useBreakpoint } from '@gn-audio/jss-components';

import {
  ancTypes,
  ancTypeToHeight,
  ancTypeToWidth,
} from 'composables/Product/ANCTypes/ANCTypesConstants';
import { ANCTypesFields } from 'composables/Product/ANCTypes/ANCTypesTypes';

type ANCTypesButtonsProps = {
  onClick: (type: ancTypes) => void;
  animationClassName: string;
  activeType: ancTypes;
  ancTypesList: ancTypes[];
  fields: ANCTypesFields;
};
export const ANCTypesButtons = ({
  animationClassName,
  onClick,
  activeType,
  ancTypesList,
  fields,
}: ANCTypesButtonsProps) => {
  const [focusedType, setFocusedType] = useState<ancTypes | null>(null);
  const { isBelowXxs } = useBreakpoint('xxs', true);

  return isBelowXxs ? (
    <MobileView
      activeType={activeType}
      focusedType={focusedType}
      animationClassName={animationClassName}
      ancTypesList={ancTypesList}
      fields={fields}
      onClick={onClick}
      changeFocus={setFocusedType}
    />
  ) : (
    <DesktopView
      activeType={activeType}
      focusedType={focusedType}
      animationClassName={animationClassName}
      ancTypesList={ancTypesList}
      fields={fields}
      onClick={onClick}
      changeFocus={setFocusedType}
    />
  );
};

type ViewProps = {
  focusedType: ancTypes | null;
  changeFocus: (type: ancTypes | null) => void;
} & ANCTypesButtonsProps;

const MobileView = ({
  animationClassName,
  onClick,
  activeType,
  focusedType,
  ancTypesList,
  fields,
  changeFocus,
}: ViewProps) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const { key } = event;
    const focusedItemIndex = focusedType
      ? ancTypesList.indexOf(focusedType)
      : ancTypesList.indexOf(activeType);

    if (key === 'ArrowDown') {
      changeFocus(ancTypesList[focusedItemIndex - 1] || null);
    } else if (key === 'ArrowUp') {
      changeFocus(ancTypesList[focusedItemIndex + 1] || null);
    }
  };

  return (
    <div
      className="ml-6 flex rounded-full bg-beige-100 p-[7px] shadow-[0_2px_2px_0px_rgba(0,0,0,0.2)_inset]"
      onKeyDown={handleKeyDown}
    >
      <div className="relative flex h-[335px] w-16 flex-col-reverse items-center">
        <div
          className={classNames(
            'absolute w-full rounded-full bg-gradient-to-t from-[#fff6cc] to-primary',
            animationClassName,
            ancTypeToHeight[activeType]
          )}
        />

        {ancTypesList.map((type) => {
          return (
            <div
              key={`anc-types-button-${type}`}
              className="flex h-1/4 items-center justify-center"
            >
              <ANCButton
                type={type}
                onClick={onClick}
                fields={fields}
                ancTypesList={ancTypesList}
                animationClassName={animationClassName}
                activeType={activeType}
                focused={type === focusedType}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DesktopView = ({
  animationClassName,
  onClick,
  activeType,
  focusedType,
  ancTypesList,
  fields,
  changeFocus,
}: ViewProps) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const { key } = event;
    const focusedItemIndex = focusedType
      ? ancTypesList.indexOf(focusedType)
      : ancTypesList.indexOf(activeType);

    if (key === 'ArrowLeft') {
      changeFocus(ancTypesList[focusedItemIndex - 1] || focusedType);
    } else if (key === 'ArrowRight') {
      changeFocus(ancTypesList[focusedItemIndex + 1] || focusedType);
    }
  };

  return (
    <div
      className="flex w-full flex-row rounded-full bg-beige-100 p-[7px] shadow-[0_2px_2px_0px_rgba(0,0,0,0.2)_inset]"
      onKeyDown={handleKeyDown}
    >
      <div className="relative flex h-16 w-full items-center">
        <div
          className={classNames(
            'absolute h-16 rounded-full bg-gradient-to-r from-[#fff6cc] to-primary',
            animationClassName,
            ancTypeToWidth[activeType]
          )}
        />

        {ancTypesList.map((type) => {
          return (
            <div key={`anc-types-button-${type}`} className="flex w-1/4 justify-center">
              <ANCButton
                type={type}
                onClick={onClick}
                fields={fields}
                ancTypesList={ancTypesList}
                animationClassName={animationClassName}
                activeType={activeType}
                focused={type === focusedType}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

type ANCButton = {
  type: ancTypes;
  focused: boolean;
} & Partial<ANCTypesButtonsProps>;

const ANCButton = ({
  animationClassName,
  onClick,
  activeType,
  type,
  fields,
  focused,
}: ANCButton) => {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (focused) {
      ref.current?.focus();
    }
  }, [focused]);

  return (
    <button
      className={classNames(
        'relative flex h-16 w-16 origin-center items-center justify-center rounded-full border-2 border-gray-700',
        !fields?.static.value && activeType !== type && 'hover:border-opacity-100 hover:bg-white',
        activeType === type
          ? 'h-[70px] w-[70px] bg-gray-700'
          : 'border-opacity-20 focus:border-opacity-20 focus-visible:bg-white',
        animationClassName
      )}
      onClick={() => onClick?.(type)}
      disabled={fields?.static.value}
      ref={ref}
    >
      <div className={classNames(activeType === type && 'invert', animationClassName)}>
        <Image field={fields?.[`iconType${type}`]} width="48" height="48" />
      </div>
    </button>
  );
};
